// Wallpaper picker: a thumbnail gallery (None / presets / solid / link / custom), an upload
// button, and the presentation knobs (fit, blur, dim, where it shows). Settings > Look and feel
// lays them out; the knobs only show for the kinds of wallpaper they change (shared/settings-index.js).
// Everything writes the shared store, so the app tab applies it live.
import { select, color, textInput, range, button, toggle } from "../ui.js";
import { get, set, subscribe } from "../../shared/settings-store.js";
import { safeUrl } from "../../app/wallpaper.js";
import { foldable } from "./bundles.js";
import { wallpaperPresets } from "../picker-lists.js";
import {
	getWallpaperImage,
	setWallpaperImageFromFile,
	clearWallpaperImage,
	subscribeWallpaperImage,
} from "../../shared/wallpaper-image.js";
import { getMediaInfo, setMediaFromFile, clearMedia, subscribeMedia, sniffKind, getMediaBlob, listSlots, subscribeSlots, saveSlot, slotBlob, deleteSlot, useMediaSlot, SLOT_MAX } from "../../shared/wallpaper-media.js";
import { setWallpaperImageData } from "../../shared/wallpaper-image.js";
import { cachedPerks, SUPPORTER_KEY } from "../../shared/supporter.js";

// Restrict a tile's `background` to a real CSS color - the shorthand also accepts
// url(), so an unvalidated wallpaperSolid could smuggle in an external fetch.
function safeColor(v) {
	return typeof CSS !== "undefined" && CSS.supports && CSS.supports("color", v) ? v : "#0a0a0a";
}
const ICON_PLAY = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M10 9.5v5l4.5-2.5z" fill="currentColor"/></svg>';
// Keys whose change requires rebuilding the gallery (selection + tile previews).
const GALLERY_KEYS = ["wallpaper", "wallpaperSolid", "wallpaperUrl"];

const ICON_NONE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><line x1="5.5" y1="5.5" x2="18.5" y2="18.5"/></svg>';
const ICON_LINK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M9 15l6-6"/><path d="M8 11l-2 2a3 3 0 0 0 4 4l2-2"/><path d="M16 13l2-2a3 3 0 0 0-4-4l-2 2"/></svg>';
const ICON_UP = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V5"/><path d="M7 10l5-5 5 5"/><path d="M5 19h14"/></svg>';
const CHECK = '<svg class="rv-wall-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="5,13 10,18 19,7"/></svg>';


export function wallpaperControls() {
	// Hidden file input drives both the upload tiles and the upload button.
	const file = document.createElement("input");
	file.type = "file";
	file.accept = "image/*,video/mp4,video/webm";
	file.hidden = true;
	file.tabIndex = -1;
	file.setAttribute("aria-hidden", "true");

	let uploadBtn; // referenced by the picker so its label can show progress
	async function pickFile() {
		file.value = ""; // allow re-selecting the same file
		file.click();
	}
	file.addEventListener("change", async () => {
		const f = file.files && file.files[0];
		if (!f) return;
		const label = uploadBtn && uploadBtn.textContent;
		if (uploadBtn) { uploadBtn.textContent = "Processing…"; uploadBtn.disabled = true; }
		try {
			// GIFs and videos keep their motion (stored whole in IndexedDB); other images are
			// downscaled into a still, as before. The file's own bytes decide, not its name.
			const kind = sniffKind(await f.slice(0, 16).arrayBuffer());
			if (kind) { await setMediaFromFile(f); set("wallpaper", "animated"); }
			else if (/^video\//.test(f.type || "")) throw new Error("That video format isn't supported. Use MP4 or WebM.");
			else { await setWallpaperImageFromFile(f); set("wallpaper", "custom"); }
		} catch (e) {
			alert(e && e.message ? e.message : "Couldn’t use that file.");
		} finally {
			if (uploadBtn) { uploadBtn.textContent = label; uploadBtn.disabled = false; }
		}
	});

	// --- gallery ---------------------------------------------------------
	const gallery = document.createElement("div");
	gallery.className = "rv-walls";
	gallery.id = "rv-walls";
	gallery.setAttribute("role", "group");
	gallery.setAttribute("aria-label", "Wallpapers");

	function makeTile({ key, label, bg, cls, icon, onClick }) {
		const b = document.createElement("button");
		b.type = "button";
		b.className = "rv-wall" + (cls ? " " + cls : "") + (get("wallpaper") === key ? " active" : "");
		if (bg) b.style.background = bg;
		b.title = label;
		b.setAttribute("aria-label", label);
		b.setAttribute("aria-pressed", String(get("wallpaper") === key));
		const cap = document.createElement("span");
		cap.className = "rv-wall-cap";
		cap.innerHTML = (icon ? icon : "") + "<span>" + label + "</span>";
		b.append(cap);
		b.insertAdjacentHTML("beforeend", CHECK);
		b.addEventListener("click", onClick);
		return b;
	}

	function renderGallery() {
		const refocus = gallery.contains(document.activeElement); // keep keyboard focus across rebuilds
		gallery.textContent = "";
		const tiles = [makeTile({ key: "none", label: "None", cls: "none", icon: ICON_NONE, onClick: () => set("wallpaper", "none") })];
		wallpaperPresets(get("wallpaper")).forEach((w) => { // a seasonal one only during its week
			const bg = w.kind === "gradient" ? w.value : `center/cover no-repeat url("${w.value}")`;
			tiles.push(makeTile({ key: w.id, label: w.label, bg, onClick: () => set("wallpaper", w.id) }));
		});
		tiles.push(makeTile({ key: "solid", label: "Solid", bg: safeColor(get("wallpaperSolid") || "#0a0a0a"), onClick: () => set("wallpaper", "solid") }));
		const urlSafe = safeUrl(get("wallpaperUrl"));
		tiles.push(makeTile({
			key: "url",
			label: "Link",
			cls: urlSafe ? "" : "action", // photo thumbnail uses the caption-bar style; the empty state is an icon tile
			bg: urlSafe ? `center/cover no-repeat url("${urlSafe}")` : "",
			icon: urlSafe ? "" : ICON_LINK,
			onClick: () => set("wallpaper", "url"),
		}));
		const img = getWallpaperImage();
		tiles.push(makeTile({
			// When no image is stored this is an "Upload" action tile - give it a
			// sentinel key so it never reads as the active "custom" selection (e.g.
			// after importing settings that point at an image this browser lacks).
			key: img ? "custom" : "__upload",
			label: img ? "Custom" : "Upload",
			cls: img ? "" : "action",
			bg: img ? `center/cover no-repeat url("${img}")` : "",
			icon: img ? "" : ICON_UP,
			onClick: () => { if (getWallpaperImage()) set("wallpaper", "custom"); else pickFile(); },
		}));
		const media = getMediaInfo();
		tiles.push(makeTile({
			key: media ? "animated" : "__upload_media",
			label: media ? "Animated" : "GIF / video",
			cls: media ? "" : "action",
			bg: media && media.poster ? `center/cover no-repeat url("${media.poster}")` : "",
			icon: media ? "" : ICON_PLAY,
			onClick: () => { if (getMediaInfo()) set("wallpaper", "animated"); else pickFile(); },
		}));
		gallery.append(...tiles);
		if (refocus) { const a = gallery.querySelector(".rv-wall.active") || gallery.firstChild; if (a) a.focus(); }
	}
	renderGallery();
	const refresh = (_s, detail) => {
		// Rebuilding the gallery re-parses the (multi-MB) custom data URL, so only do it when the
		// selection or a tile preview actually changed, not on every blur/dim/accent tick.
		const keys = detail && detail.keys;
		if (!keys || detail.reset || detail.remote || keys.some((k) => GALLERY_KEYS.includes(k))) renderGallery();
		removeBtn.hidden = !getWallpaperImage();
		removeMediaBtn.hidden = !getMediaInfo();
	};

	uploadBtn = button("Upload…", pickFile);
	const removeBtn = button("Remove image", () => { clearWallpaperImage(); if (get("wallpaper") === "custom") set("wallpaper", "none"); }, "rv-btn-danger");
	const removeMediaBtn = button("Remove GIF or video", async () => { await clearMedia(); if (get("wallpaper") === "animated") set("wallpaper", "none"); }, "rv-btn-danger");
	const uploadControl = document.createElement("div");
	uploadControl.className = "rv-btnrow";
	uploadControl.append(uploadBtn, removeBtn, removeMediaBtn, file);
	refresh();
	subscribe(refresh);
	subscribeWallpaperImage(refresh);
	subscribeMedia(() => refresh(null, { reset: true }));

	// Saved wallpapers (docs/SUPPORTERS.md 8). Free stays exactly as it is: one picture and one
	// GIF or video. With a pass, up to 10 of your own can be saved and switched between. When a
	// pass ends, nothing is deleted: the list stays, with Download and Delete.
	const slotsWrap = document.createElement("div");
	slotsWrap.className = "rv-slots-wrap";
	const slotsDesc = document.createElement("p");
	slotsDesc.className = "rv-row-desc";
	const slotsBox = document.createElement("div");
	slotsBox.className = "rv-slots";
	const slotsMsg = document.createElement("p");
	slotsMsg.className = "rv-row-desc";
	slotsMsg.setAttribute("role", "status");
	slotsWrap.append(slotsDesc, slotsBox, slotsMsg);
	const dataUrlToBlob = async (u) => (await fetch(u)).blob();
	const blobToDataUrl = (b) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(b); });
	function renderSlots() {
		const perk = cachedPerks().wallpaperSlots > 1;
		const list = listSlots();
		slotsBox.textContent = "";
		slotsDesc.textContent = perk ? "Save up to " + SLOT_MAX + " of your own wallpapers here and switch between them. Stored only in this browser."
			: list.length ? "Renew your pass to switch between saved wallpapers. You can still download or delete them."
			: "With a supporter pass you can save up to " + SLOT_MAX + " of your own wallpapers and switch between them.";
		if (perk) {
			const w = get("wallpaper");
			const save = button("Save this wallpaper", async () => {
				slotsMsg.textContent = "";
				try {
					if (get("wallpaper") === "custom" && getWallpaperImage()) await saveSlot({ kind: "image", blob: await dataUrlToBlob(getWallpaperImage()), name: "Picture", poster: "" });
					else if (get("wallpaper") === "animated" && getMediaInfo()) { const m = getMediaInfo(); const b = await getMediaBlob(); if (!b) throw new Error("The file isn't in this browser."); await saveSlot({ kind: m.kind, blob: b, name: m.name || "Animated", poster: m.poster }); }
					slotsMsg.textContent = "Saved.";
				} catch (e) { slotsMsg.textContent = e.message || "Couldn't save it."; }
			});
			save.disabled = !(w === "custom" || w === "animated") || list.length >= SLOT_MAX;
			slotsBox.append(save);
		}
		for (const it of list) {
			const item = document.createElement("div");
			item.className = "rv-slot";
			const label = document.createElement("span");
			label.textContent = (it.kind === "image" ? "Picture" : it.kind.toUpperCase()) + (it.name && it.name !== "Picture" ? ": " + it.name : "") + ", saved " + new Date(it.addedAt).toLocaleDateString();
			item.append(label);
			if (perk) item.append(button("Use", async () => {
				try {
					if (it.kind === "image") { const rec = await slotBlob(it.id); if (!rec) throw new Error("That file isn't in this browser any more."); setWallpaperImageData(await blobToDataUrl(rec.blob)); set("wallpaper", "custom"); }
					else { await useMediaSlot(it.id); set("wallpaper", "animated"); }
					slotsMsg.textContent = "Wallpaper changed.";
				} catch (e) { slotsMsg.textContent = e.message || "Couldn't use it."; }
			}));
			item.append(button("Download", async () => {
				const rec = await slotBlob(it.id);
				if (!rec) { slotsMsg.textContent = "That file isn't in this browser any more."; return; }
				const a = document.createElement("a");
				a.href = URL.createObjectURL(rec.blob);
				a.download = "revault-wallpaper." + (it.kind === "image" ? "jpg" : it.kind);
				document.body.append(a); a.click(); a.remove();
				setTimeout(() => URL.revokeObjectURL(a.href), 2000);
			}), button("Delete", async () => { await deleteSlot(it.id); slotsMsg.textContent = "Deleted."; }, "rv-btn-danger"));
			slotsBox.append(item);
		}
	}
	renderSlots();
	subscribeSlots(renderSlots);
	subscribe(renderSlots);
	window.addEventListener("storage", (e) => { if (e.key === SUPPORTER_KEY) renderSlots(); });

	return {
		wallpaper: () => foldable(gallery, () => gallery.children.length, "wallpapers"),
		wallpaperUpload: () => uploadControl,
		wallpaperSlots: () => slotsWrap,
		wallpaperUrl: () => textInput("wallpaperUrl", "https://…/photo.jpg"),
		wallpaperSolid: () => color("wallpaperSolid"),
		wallpaperFit: () => select("wallpaperFit", [["cover", "Cover"], ["contain", "Contain"], ["center", "Center"], ["tile", "Tile"]]),
		wallpaperBlur: () => range("wallpaperBlur", 0, 40, 1, (v) => v + "px"),
		wallpaperDim: () => range("wallpaperDim", 0, 85, 1, (v) => v + "%"),
		wallpaperScope: () => select("wallpaperScope", [["start", "Start page"], ["app", "All of Re:Vault"]]),
		wallpaperAnimate: () => toggle("wallpaperAnimate"),
		snowfall: () => toggle("snowfall"),
	};
}
